/**
 * RetakeButton Component
 *
 * Main component for course retake functionality.
 * Displays the retake button and manages the retake modal.
 *
 * Features:
 * - Checks eligibility on mount
 * - Handles both full and partial reset
 * - Comprehensive error handling with user-friendly messages
 * - Hebrew RTL support
 * - Loading, error, and success states
 *
 * @version 2.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Button, Spinner, Icon } from '@edx/paragon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { Loop } from '@edx/paragon/icons';

import RetakeModal from './RetakeModal';
import messages from '../messages';
import {
  fetchEligibility,
  initiateReset,
  ErrorTypes,
  RetakeApiError,
  transformEligibilityResponse,
} from './retakeService';

import './button.css';

// ===== Constants =====

const RELOAD_DELAY_MS = 2000;

// ===== Helper Components =====

/**
 * Section wrapper component
 */
const RetakeSection = ({ children, dir }) => (
  <section className="mb-4" dir={dir}>
    <div id="retake-course" className="retake-button-container mb-4">
      {children}
    </div>
  </section>
);

RetakeSection.propTypes = {
  children: PropTypes.node.isRequired,
  dir: PropTypes.string,
};

RetakeSection.defaultProps = {
  dir: undefined,
};

/**
 * Error display component with retry option
 */
const ErrorDisplay = ({ message, onRetry, intl }) => (
  <RetakeSection>
    <div className="retake-error">
      <FontAwesomeIcon icon={faExclamationCircle} className="retake-error__icon" />
      <small className="text-danger">{message}</small>
      {onRetry && (
        <Button
          variant="link"
          size="sm"
          onClick={onRetry}
          className="retake-error__retry"
        >
          {intl.formatMessage(messages.retakeButtonRetry)}
        </Button>
      )}
    </div>
  </RetakeSection>
);

ErrorDisplay.propTypes = {
  message: PropTypes.string.isRequired,
  onRetry: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

ErrorDisplay.defaultProps = {
  onRetry: null,
};

// ===== Main Component =====

/**
 * RetakeButton - Main entry point for course retake UI
 */
const RetakeButton = ({ courseId }) => {
  const intl = useIntl();
  // ===== State =====
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  // ===== API Calls =====

  /**
   * Fetch eligibility data from API
   */
  const loadEligibility = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);

      const response = await fetchEligibility(courseId);
      const transformed = transformEligibilityResponse(response);

      setEligibilityData(transformed);
    } catch (error) {
      // Handle specific error types
      if (error instanceof RetakeApiError) {
        switch (error.type) {
          case ErrorTypes.UNAUTHORIZED:
            setLoadError('יש להתחבר מחדש כדי להמשיך');
            break;
          case ErrorTypes.FORBIDDEN:
            setLoadError('אין לך הרשאה לצפות במידע זה');
            break;
          case ErrorTypes.NOT_FOUND:
            setLoadError('הקורס לא נמצא');
            break;
          case ErrorTypes.SERVER_ERROR:
            setLoadError('שגיאת שרת. נסה שוב מאוחר יותר');
            break;
          case ErrorTypes.NETWORK_ERROR:
            setLoadError('שגיאת רשת. בדוק את החיבור לאינטרנט');
            break;
          default:
            setLoadError(error.userMessage || 'שגיאה בטעינת נתונים');
        }
      } else {
        setLoadError('שגיאה בטעינת נתונים');
      }

      setEligibilityData(null);
    } finally {
      setIsLoading(false);
    }
  }, [courseId]);

  /**
   * Handle reset confirmation from modal
   */
  const handleConfirmReset = useCallback(async ({ resetType, chapterIds }) => {
    try {
      setIsProcessing(true);
      setResetError(null);

      const response = await initiateReset(courseId, resetType, chapterIds);

      if (response.status === 'success') {
        setResetSuccess(true);

        // Reload page after delay to show success message
        setTimeout(() => {
          setIsModalOpen(false);
          window.location.reload();
        }, RELOAD_DELAY_MS);
      } else {
        // API returned success:false
        setResetError(response.message || 'שגיאה בהפעלת איפוס הקורס');
      }
    } catch (error) {
      // Handle specific error types
      if (error instanceof RetakeApiError) {
        switch (error.type) {
          case ErrorTypes.UNAUTHORIZED:
            setResetError('פג תוקף ההתחברות. יש להתחבר מחדש');
            break;
          case ErrorTypes.FORBIDDEN:
            setResetError(error.data?.message || 'אין לך הרשאה לבצע איפוס');
            break;
          case ErrorTypes.BAD_REQUEST:
            setResetError(error.data?.message || 'הבקשה אינה תקינה');
            break;
          case ErrorTypes.NOT_FOUND:
            setResetError('הקורס או הפרקים לא נמצאו');
            break;
          case ErrorTypes.SERVER_ERROR:
            setResetError('שגיאת שרת. נסה שוב מאוחר יותר');
            break;
          case ErrorTypes.NETWORK_ERROR:
            setResetError('שגיאת רשת. בדוק את החיבור לאינטרנט');
            break;
          default:
            setResetError(error.userMessage || 'שגיאה בהפעלת איפוס הקורס');
        }
      } else {
        setResetError('שגיאה בהפעלת איפוס הקורס');
      }
    } finally {
      setIsProcessing(false);
    }
  }, [courseId]);

  // ===== Effects =====

  // Load eligibility on mount
  useEffect(() => {
    if (courseId) {
      loadEligibility();
    }
  }, [courseId, loadEligibility]);

  // ===== Handlers =====

  /**
   * Open the retake modal
   */
  const handleOpenModal = useCallback(() => {
    setResetError(null);
    setResetSuccess(false);
    setIsModalOpen(true);
  }, []);

  /**
   * Close the retake modal
   */
  const handleCloseModal = useCallback(() => {
    if (!isProcessing) {
      setIsModalOpen(false);
      setResetError(null);
    }
  }, [isProcessing]);

  /**
   * Retry loading eligibility after error
   */
  const handleRetryLoad = useCallback(() => {
    loadEligibility();
  }, [loadEligibility]);

  // ===== Render Helpers =====

  /**
   * Format date for Hebrew display
   */
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      return dateString;
    }
  };

  // ===== Render States =====

  // Loading state
  if (isLoading) {
    return (
      <RetakeSection>
        <Spinner animation="border" size="sm" className="ml-2" />
        <small className="text-muted mr-2">{intl.formatMessage(messages.retakeButtonLoading)}</small>
      </RetakeSection>
    );
  }

  // Load error state
  if (loadError) {
    return (
      <ErrorDisplay
        message={loadError}
        onRetry={handleRetryLoad}
        intl={intl}
      />
    );
  }

  // No eligibility data
  if (!eligibilityData) {
    return null;
  }

  // ===== Case 1: Retake NOT enabled =====
  if (!eligibilityData.retakeEnabled) {
    return (
      <RetakeSection>
        <small className="text-muted">
          {intl.formatMessage(messages.retakeButtonNotAvailable)}
        </small>
      </RetakeSection>
    );
  }

  // ===== Case 2: Has Certificate - Cannot Reset =====
  if (eligibilityData.hasCertificate) {
    return (
      <RetakeSection>
        <small className="text-muted">
          {intl.formatMessage(messages.retakeButtonHasCertificate)}
        </small>
        {eligibilityData.certificateUrl && (
          <div className="mt-2">
            <a
              href={eligibilityData.certificateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary"
            >
              {intl.formatMessage(messages.retakeButtonViewCertificate)}
            </a>
          </div>
        )}
      </RetakeSection>
    );
  }

  // ===== Case 3: Eligible - Show Button =====
  if (eligibilityData.isEligible) {
    return (
      <>
        <RetakeSection dir={intl.locale === 'he' ? 'rtl' : 'ltr'}>
          <Button
            variant="link"
            size="inline"
            onClick={handleOpenModal}
          >
            <Icon
              src={Loop}
              className="mr-2"
              style={{ height: '18px', width: '18px' }}
            />
            {intl.formatMessage(messages.retakeButtonResetAndRestart)}
          </Button>

          {/* Partial reset hint */}
          {eligibilityData.allowPartialReset && eligibilityData.chapters?.length > 0 && (
            <small className="text-muted d-block mt-1">
              {intl.formatMessage(messages.retakeButtonPartialHint)}
            </small>
          )}

          {/* Remaining retakes info */}
          {eligibilityData.maxRetakes && eligibilityData.resetsRemaining !== null && (
            <small className="text-muted d-block mt-1">
              {intl.formatMessage(messages.retakeButtonResetsRemaining, {
                remaining: eligibilityData.resetsRemaining,
                total: eligibilityData.maxRetakes,
              })}
            </small>
          )}
        </RetakeSection>

        <RetakeModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmReset}
          isProcessing={isProcessing}
          error={resetError}
          success={resetSuccess}
          allowPartialReset={eligibilityData.allowPartialReset}
          chapters={eligibilityData.chapters || []}
          isLoadingChapters={false}
          chaptersError={null}
        />
      </>
    );
  }

  // ===== Case 4: In Cooldown =====
  if (eligibilityData.nextAvailableDate) {
    return (
      <RetakeSection>
        <FontAwesomeIcon icon={faClock} className="ml-2" fixedWidth />
        <small className="text-muted">
          {eligibilityData.message || `זמין מתאריך: ${formatDate(eligibilityData.nextAvailableDate)}`}
        </small>
      </RetakeSection>
    );
  }

  // ===== Case 5: Max Retakes Reached =====
  if (eligibilityData.resetsRemaining === 0) {
    return (
      <RetakeSection>
        <small className="text-muted">
          {eligibilityData.message || intl.formatMessage(messages.retakeButtonMaxResets)}
        </small>
      </RetakeSection>
    );
  }

  // ===== Case 6: Not Completed =====
  if (!eligibilityData.hasCompleted) {
    return (
      <RetakeSection>
        <small className="text-muted">
          {eligibilityData.message || intl.formatMessage(messages.retakeButtonNotCompleted)}
        </small>
      </RetakeSection>
    );
  }

  // ===== Fallback: Show Message =====
  if (eligibilityData.message) {
    return (
      <RetakeSection>
        <small className="text-muted">
          {eligibilityData.message}
        </small>
      </RetakeSection>
    );
  }

  // No render needed
  return null;
};

RetakeButton.propTypes = {
  /** Course ID string (e.g., 'course-v1:ORG+COURSE+RUN') */
  courseId: PropTypes.string.isRequired,
};

export default RetakeButton;
